// Display helpers for the Activity and Notifications sections (spec 9.3). Plain strings,
// American English, no em dashes.
import { customerSectionPath } from './navigation.js';

export const SEVERITY_LABEL = Object.freeze({ critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' });
export const SEVERITY_TONE = Object.freeze({ critical: 'badge-red', high: 'badge-amber', medium: 'badge-amber', low: 'badge-slate', info: 'badge-slate' });
export const SEVERITY_ORDER = Object.freeze(['critical', 'high', 'medium', 'low', 'info']);
export const CATEGORY_LABELS = Object.freeze({
  'my-account': 'My account', groups: 'Groups', apps: 'Applications', roles: 'Roles', pim: 'Privileged access (PIM)', tenant: 'Tenant settings', guests: 'Guests', integrity: 'Watch integrity',
});
// 'aged-out' is not an answer: nobody answered within the customer's aging period, so onGuard closed the question.
export const ANSWER_LABEL = Object.freeze({ unanswered: 'Not answered', yes: 'Expected', no: 'Not expected', 'not-applicable': 'No answer needed', 'aged-out': 'Aged out' });

/** Local time only, e.g. "Sep 30, 2026, 7:00 AM". */
export function formatLocalWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown time';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Local time with the UTC time in brackets, e.g. "Sep 30, 2026, 7:00 AM (11:00 UTC)". */
export function formatWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown time';
  return `${formatLocalWhen(iso)} (${d.toISOString().slice(11, 16)} UTC)`;
}

/** Lag as text: "under 1 min" below a minute, whole minutes otherwise. */
export function formatLag(seconds) {
  if (!Number.isFinite(seconds)) return 'Not measured';
  return seconds < 60 ? 'under 1 min' : `${Math.round(seconds / 60)} min`;
}

/** /customers/:id/activity[/sub]: the Watch events, flagged cases and event pages (ENTRAID-347 plan C3). */
export function activityPath(customerId, sub = '') {
  const base = customerSectionPath(customerId, 'activity');
  return sub ? `${base}/${sub}` : base;
}

/** /customers/:id/notifications[/sub]. */
export function notificationsPath(customerId, sub = '') {
  const base = customerSectionPath(customerId, 'notifications');
  return sub ? `${base}/${sub}` : base;
}

export const AUDIENCE_LABEL = Object.freeze({ self: 'Me', owners: 'Owners', guardians: 'Guardians', admins: 'Administrators' });
export const CADENCE_LABEL = Object.freeze({ immediate: 'Immediate', digest: 'Digest' });
export const STATUS_LABEL = Object.freeze({
  queued: 'Queued', 'held-digest': 'In the next digest', 'held-ceiling': 'Held (volume cap)', sending: 'Sending', sent: 'Sent', delivered: 'Delivered',
  'failed-retrying': 'Retrying', failed: 'Failed', bounced: 'Bounced', complained: 'Reported as spam', rejected: 'Rejected', delayed: 'Delayed',
  'suppressed-classic': 'Suppressed (classic unsubscribe)', 'suppressed-unsubscribe': 'Suppressed (unsubscribed)', 'suppressed-preference': 'Suppressed (preference)',
  'suppressed-bounced': 'Suppressed (address bounced)', 'suppressed-complaint': 'Suppressed (reported as spam)', 'no-address': 'No address', 'audience-off': 'Audience off', 'suppressed-paused': 'Paused (delivery quality)',
});
export const SCOPE_LABEL = Object.freeze({ classic: 'Classic rules', owners: 'Owner notices unsubscribe', guardians: 'Guardians', self: 'Me', 'address-bounced': 'Bounced address', complained: 'Reported as spam' });
export const CASE_STATE_LABEL = Object.freeze({ open: 'Open', acknowledged: 'Acknowledged', resolved: 'Resolved' });
export const CASE_OUTCOME_LABEL = Object.freeze({ 'expected-after-review': 'Expected after review', 'incident-confirmed': 'Incident confirmed', 'false-positive': 'False positive' });

/** The catalog label for an event id, from GET /watch's catalog; a readable fallback otherwise. */
export function eventLabelFrom(catalog, catalogId) {
  const hit = catalog?.events?.find((e) => e.id === catalogId);
  if (hit?.label) return hit.label;
  const s = String(catalogId ?? '').replace(/[.-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown event';
}

/** A row's status; a blocked row shows its reason code. */
export function statusText(row) {
  if (row.status === 'blocked-recipient') return `Blocked: ${row.reason ?? 'recipient check'}`;
  return STATUS_LABEL[row.status] ?? row.status;
}

export const GUARDIAN_STATUS_LABEL = Object.freeze({
  valid: 'Valid',
  pending: 'Checking, within 15 minutes',
  'not-found': 'Not found in the directory',
  ambiguous: 'More than one directory match',
  guest: 'Guests cannot be guardians',
  disabled: 'Account disabled',
  'no-address': 'No mailbox address',
  'outside-verified-domains': 'Address outside your verified domains',
  unsubscribed: 'Unsubscribed',
});
